/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useMemo } from "react";
import { BEM } from "@adapptio/lib-components";
import { Icon } from "../Icon";

import "./Breadcrumbs.scss";

export interface IBreadcrumbsProps {
	ariaLabel?: string;
	className?: string;
	children?: React.ReactNode;
	separator?: React.ReactNode;
	maxItems?: number;
}

function insertSeparators(items, className, separator) {
	return items.reduce((acc, current, index) => {
		if (index < items.length - 1) {
			acc = acc.concat(
				current,
				<li aria-hidden key={`separator-${index}`} className={className}>
					{separator}
				</li>
			);
		} else {
			acc.push(current);
		}

		return acc;
	}, []);
}

export const Breadcrumbs: React.FC<IBreadcrumbsProps> = ({
	children,
	className,
	ariaLabel,
	maxItems,
	separator
}) => {
	const baseClass = BEM("edt-breadcrumbs");

	const separatorCmp = useMemo(
		() =>
			separator || (
				<Icon name="mdi/menu-right" className={baseClass.element("separator-icon").toClassName()} />
			),
		[ separator ]
	);

	const allItems = React.Children.toArray(children)
		.filter((child) => React.isValidElement(child))
		.map((child, index) => (
			<li className={baseClass.element("item").toClassName()} key={`child-${index}`}>
				{child}
			</li>
		));

	if (className) {
		baseClass.add(className);
	}

	return (
		<nav className={baseClass.toClassName()} aria-label={ariaLabel}>
			<ol className={baseClass.element("list").toClassName()}>
				{insertSeparators(allItems, baseClass.element("separator").toClassName(), separatorCmp)}
			</ol>
		</nav>
	);
};

Breadcrumbs.defaultProps = {
	ariaLabel: "breadcrumb",
	maxItems: 8
};
